/**
 * 인증/멤버/유저 API (P6 — Admin Login, P7-4 — Active Assignee).
 *
 * login / changePassword 함수 — auth 페이지에서 호출.
 * (구 api/deal.ts 의 UserAssignee/updateUserPhone/fetchActiveAssignees 흡수)
 */

import { z } from 'zod';
import { clearAuth, setAuthState, type AuthState } from '../lib/auth';
import { API_BASE, authHeaders, handleUnauthorized, getJson, postJson, parseResponse } from './http';

// ─── 인증 API (P6 — Admin Login) ──────────────────────────────────────
/** POST /api/auth/login 응답 — backend shared/auth.schema.ts 의 LoginResponse.
 *  인증 응답은 이후 모든 요청의 신뢰 기반이라 특히 엄격하게 검증. */
const LoginResponseSchema = z.object({
  accessToken:          z.string().min(1),
  refreshToken:         z.string().min(1),
  accessExpiresAt:      z.string(),
  refreshExpiresAt:     z.string(),
  must_change_password: z.boolean(),
});
export type LoginResponse = z.infer<typeof LoginResponseSchema>;

/**
 * 로그인 — 이메일 + 비번 → 토큰 + must_change_password.
 *
 * 실패 시:
 *   - 401: '이메일 또는 비밀번호가 잘못되었습니다' throw
 *   - 400: zod 검증 실패 (이메일 형식 등)
 *
 * 성공 시 호출자가 setAuthState() 로 저장 + 라우터 navigate.
 */
export async function authLogin(email: string, password: string): Promise<LoginResponse> {
  const res = await fetch(`${API_BASE}/api/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) {
    let msg = '로그인 실패';
    try {
      // backend errorHandler.ts 응답 형식: { error: { code, message, details } }
      // 일부 구 endpoint 호환: { error: 'string' }
      const body = (await res.json()) as {
        error?: string | { message?: string };
      };
      if (typeof body.error === 'string') {
        msg = body.error;
      } else if (body.error?.message) {
        msg = body.error.message;
      }
    } catch { /* ignore */ }
    throw new Error(msg);
  }
  return parseResponse(await res.json(), LoginResponseSchema, 'POST /api/auth/login');
}

/**
 * 비밀번호 변경 — 현재 비번 + 새 비번 → 토큰 재발급 + must_change_password=false.
 *
 * 인증 필요 (Authorization 헤더). 라우터가 인증된 상태에서만 호출.
 *
 * 실패 시:
 *   - 403: 현재 비번 불일치
 *   - 400: zod 정책 위반 (8자 미만 / 영문 없음 / default 사용 등)
 *
 * 성공 시 새 토큰 + must_change_password=false 응답.
 * 호출자가 setAuthState() 로 갱신.
 */
export async function authChangePassword(
  currentPassword: string,
  newPassword: string,
): Promise<LoginResponse> {
  const res = await fetch(`${API_BASE}/api/auth/change-password`, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({
      current_password: currentPassword,
      new_password: newPassword,
    }),
  });
  if (res.status === 401) {
    handleUnauthorized();
    throw new Error('인증이 만료되었습니다.');
  }
  if (!res.ok) {
    let msg = '비밀번호 변경 실패';
    try {
      // backend errorHandler.ts 응답 형식: { error: { code, message, details } }
      // 일부 구 endpoint 호환: { error: 'string' }
      const body = (await res.json()) as {
        error?: string | { message?: string };
      };
      if (typeof body.error === 'string') {
        msg = body.error;
      } else if (body.error?.message) {
        msg = body.error.message;
      }
    } catch { /* ignore */ }
    throw new Error(msg);
  }
  return parseResponse(await res.json(), LoginResponseSchema, 'POST /api/auth/change-password');
}

/**
 * 로그아웃 — backend 의 jti 차단 + localStorage 정리.
 *
 * 실패해도 clearAuth 는 호출 (로컬은 정리됨).
 */
export async function authLogout(): Promise<void> {
  try {
    await fetch(`${API_BASE}/api/auth/logout`, {
      method: 'POST',
      headers: authHeaders(),
    });
  } catch { /* ignore */ }
  clearAuth();
}

// ─── 멤버 관리 (owner 전용 — 설정 페이지) ─────────────────────────────
/** 활성 멤버 (owner 전용). GET /api/admin/user/members */
const MemberSchema = z.object({
  id:                   z.string(),
  email:                z.string(),
  name:                 z.string(),
  role:                 z.string(),
  must_change_password: z.boolean(),
});
const MembersResponseSchema = z.object({ members: z.array(MemberSchema) });
export type Member = z.infer<typeof MemberSchema>;

export async function fetchMembers(): Promise<Member[]> {
  const r = await getJson<{ members: Member[] }>('/api/admin/user/members', { schema: MembersResponseSchema });
  return r.members;
}
/** 멤버 비번을 기본값(hcltech1234)으로 초기화 (owner 전용). */
export async function resetMemberPassword(userId: string): Promise<void> {
  await postJson<{ ok: boolean }>(
    `/api/admin/user/${encodeURIComponent(userId)}/reset-password`,
    {},
    z.object({ ok: z.boolean() }),
  );
}

/** login/changePassword 응답을 AuthState 로 변환해 localStorage 에 저장. */
export function saveLoginResponse(res: LoginResponse, email: string): void {
  const state: AuthState = {
    accessToken: res.accessToken,
    refreshToken: res.refreshToken,
    accessExpiresAt: res.accessExpiresAt,
    refreshExpiresAt: res.refreshExpiresAt,
    must_change_password: res.must_change_password,
    email,
  };
  setAuthState(state);
}

// ─── Active Assignee API (P7-4) ──────────────────────────────────────
// (구 api/deal.ts 에서 이동 — 로딩 인디케이터 없는 raw fetch 동작 그대로 유지)

/** raw-fetch GET 헬퍼 — http.getJson 과 달리 글로벌 로딩 펄을 띄우지 않음. */
async function getJsonLocal<T>(
  path: string,
  opts: { signal?: AbortSignal; schema?: z.ZodType<T> } = {},
): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: authHeaders(),
    signal: opts.signal,
  });
  if (res.status === 401) {
    handleUnauthorized();
    throw new Error('인증이 만료되었습니다. 다시 로그인해주세요.');
  }
  if (!res.ok) {
    let detail = '';
    try {
      const errBody = (await res.json()) as { error?: string | { message?: string } };
      if (typeof errBody.error === 'string') detail = errBody.error;
      else if (errBody.error?.message) detail = errBody.error.message;
    } catch { /* ignore */ }
    throw new Error(`HTTP ${res.status} ${detail}`.trim());
  }
  return parseResponse(await res.json(), opts.schema, `GET ${path}`);
}

/** GET /api/admin/user/assignees 응답의 한 항목. */
const UserAssigneeSchema = z.object({
  id:    z.string(),
  email: z.string(),
  name:  z.string(),
  role:  z.string(),            // 'owner' | 'leader' | 'staff'
  phone: z.string().nullable(), // 휴대폰 — 견적서 담당자 연락처 (마이그102)
});
export type UserAssignee = z.infer<typeof UserAssigneeSchema>;

/**
 * PATCH /api/admin/user/:id/phone — 영업담당자 휴대폰 수정.
 * 딜 상세 '영업담당자 연락처' 인라인 편집에서 호출. 갱신된 UserAssignee 반환.
 */
export async function updateUserPhone(
  userId: string,
  phone: string | null,
  dealId?: string | number | null,   // 딜 상세에서 편집 시 그 딜 히스토리에 기록
): Promise<UserAssignee> {
  const q = dealId != null && dealId !== '' ? `?deal_id=${encodeURIComponent(String(dealId))}` : '';
  const res = await fetch(`${API_BASE}/api/admin/user/${encodeURIComponent(userId)}/phone${q}`, {
    method: 'PATCH',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ phone }),
  });
  if (res.status === 401) { handleUnauthorized(); throw new Error('인증이 만료되었습니다.'); }
  if (!res.ok) throw new Error(`전화번호 저장 실패 (HTTP ${res.status})`);
  return parseResponse(await res.json(), UserAssigneeSchema, 'PATCH /api/admin/user/:id/phone');
}

/**
 * 영업담당 dropdown 용 — 활성 + role IN (staff/leader/owner) user list.
 * 정렬: name ASC.
 *
 * signal — 라우터 loader 에서 호출 시 AbortSignal 전달. 라우트 이동으로
 * 취소되면 fetch 가 즉시 AbortError 던지고 죽음.
 */
export async function fetchActiveAssignees(
  opts: { signal?: AbortSignal } = {},
): Promise<UserAssignee[]> {
  const r = await getJsonLocal<{ rows: UserAssignee[] }>(
    '/api/admin/user/assignees',
    { ...opts, schema: z.object({ rows: z.array(UserAssigneeSchema) }) },
  );
  return r.rows;
}
